import * as THREE from 'three'

/**
 * Roblox の独自 .mesh ファイル形式を Three.js の BufferGeometry に変換する。
 *
 * 対応:
 * - v1.00 / v1.01 (テキスト形式: `[x,y,z][nx,ny,nz][u,v]` の繰り返し)
 * - v2.00          (バイナリ: ヘッダ + 頂点配列 + 面配列)
 * - v3.00 / v3.01  (バイナリ: v2 に LOD テーブルを追加)
 * - v4.00 / v4.01  (バイナリ: 拡張ヘッダ + 40byte 頂点 + LOD/ボーン等。LOD0 のみ描画)
 *
 * v3/v4 は複数 LOD（詳細度）の面を連結して持つ。最高詳細の LOD0 だけを描画し、
 * 簡略版 LOD を重ねて描かない（重なり描画による見た目崩れを防ぐ）。
 *
 * 未対応 (将来):
 * - v5.00+（rbxl 内で使われていれば追って実装）
 * - スキニング/ボーン/FACS（静的メッシュとして頂点・面のみ使う）
 *
 * 失敗時は throw する。呼び出し側は box フォールバックを継続できる。
 */

interface ParsedMesh {
  readonly positions: Float32Array
  readonly normals: Float32Array
  readonly uvs: Float32Array
  /** インデックス（バイナリ形式）。テキスト v1 は非インデックスのため undefined。 */
  readonly indices?: Uint32Array
}

/** 与えられたテキストから version 行を読み 'major.minor' 文字列を返す。 */
const readVersion = (text: string): string => {
  const newline = text.indexOf('\n')
  if (newline < 0) {
    throw new Error('Roblox mesh: version 行が見つかりません')
  }
  const first = text.slice(0, newline).trim()
  const match = /^version\s+(\d+\.\d+)/.exec(first)
  if (!match) {
    throw new Error(`Roblox mesh: version 行を解釈できません: "${first}"`)
  }
  return match[1]
}

/**
 * v1.00 / v1.01 のテキスト形式をパースする。
 * 構造:
 *   version 1.00\n
 *   <face_count>\n
 *   [x,y,z][nx,ny,nz][u,v]   ← 1 vertex
 *   [x,y,z][nx,ny,nz][u,v]
 *   [x,y,z][nx,ny,nz][u,v]
 *   ... 各 face は 3 vertex 連続 (face_count * 3 個の vertex が並ぶ)
 *
 * UV.y は v1.00/v1.01 とも生値のまま使う。three の texture.flipY=true
 * （UV V=0 が画像下端）と Roblox v1 の規約が一致するため補正は不要。
 * 以前は v1.00 に V=1-V 補正を入れていたが、RJF03 の旗（mesh 1038492）で
 * ポールが布のテクセルを拾って赤くなる実害で誤りと確認した。
 */
const parseV1 = (text: string, version: string): ParsedMesh => {
  // ヘッダ 2 行（version, face_count）を取り除いて vertex データの本体を切り出す。
  const lines = text.split(/\r?\n/)
  if (lines.length < 3) {
    throw new Error('Roblox mesh v1: ヘッダが不足しています')
  }
  const faceCount = parseInt(lines[1].trim(), 10)
  if (!Number.isFinite(faceCount) || faceCount <= 0) {
    throw new Error(`Roblox mesh v1: face_count が不正: ${lines[1]}`)
  }
  const body = lines.slice(2).join('')

  // `[x,y,z]` を順に取り出して 3 つで 1 vertex（position / normal / uv）。
  // v1.00 は頂点座標が 2 倍で格納される仕様のため 0.5 倍で実寸へ戻す（v1.01 は等倍）。
  const positionScale = version === '1.00' ? 0.5 : 1
  const tripletRegex = /\[([^\]]+)\]/g
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  let tripletIndex = 0
  let match: RegExpExecArray | null = tripletRegex.exec(body)
  while (match !== null) {
    const parts = match[1].split(',')
    if (parts.length < 2) {
      throw new Error(`Roblox mesh v1: 不正なトリプレット: [${match[1]}]`)
    }
    const a = parseFloat(parts[0])
    const b = parseFloat(parts[1])
    const c = parts.length >= 3 ? parseFloat(parts[2]) : 0
    if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) {
      throw new Error(`Roblox mesh v1: 数値変換失敗: [${match[1]}]`)
    }
    const channel = tripletIndex % 3
    if (channel === 0) {
      positions.push(a * positionScale, b * positionScale, c * positionScale)
    } else if (channel === 1) {
      normals.push(a, b, c)
    } else {
      uvs.push(a, b)
    }
    tripletIndex += 1
    match = tripletRegex.exec(body)
  }

  const expectedTriplets = faceCount * 3 * 3 // face × 3 vertex × 3 triplets/vertex
  if (tripletIndex !== expectedTriplets) {
    throw new Error(
      `Roblox mesh v1: トリプレット数が一致しません (期待 ${expectedTriplets} / 実際 ${tripletIndex})`,
    )
  }

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    uvs: new Float32Array(uvs),
  }
}

/** バイナリ本体の開始オフセット（version 行直後）を返す。 */
const binaryBodyOffset = (bytes: Uint8Array): number => {
  // version 行は常にテキストで、改行（\n）で終わる。その直後からバイナリ。
  for (let i = 0; i < bytes.length && i < 64; i += 1) {
    if (bytes[i] === 0x0a) {
      return i + 1
    }
  }
  throw new Error('Roblox mesh(binary): version 行の終端が見つかりません')
}

/**
 * バイナリ頂点配列から position/normal/uv を取り出す。
 * 頂点先頭は常に position(3 float) + normal(3 float) + uv(2 float) = 32 byte。
 * それ以降（tangent / RGBA color など）は vertexSize に応じて読み飛ばす。
 * これにより 36 / 40 byte いずれの頂点レイアウトにも追従できる。
 */
/** 読み出し領域 [offset, offset+byteLength) がバッファ内に収まるか検証する。 */
const ensureRange = (
  view: DataView,
  offset: number,
  byteLength: number,
  label: string,
): void => {
  if (
    !Number.isSafeInteger(offset) ||
    !Number.isSafeInteger(byteLength) ||
    offset < 0 ||
    byteLength < 0 ||
    offset + byteLength > view.byteLength
  ) {
    throw new Error(`Roblox mesh(binary): ${label} がファイル範囲外です`)
  }
}

interface VertexData {
  positions: Float32Array
  normals: Float32Array
  uvs: Float32Array
  /** position が非有限だった頂点を 1 とするマスク（後段で参照面を捨てる）。 */
  invalid: Uint8Array
}

const readVertices = (
  view: DataView,
  offset: number,
  numVerts: number,
  vertexSize: number,
): VertexData => {
  const positions = new Float32Array(numVerts * 3)
  const normals = new Float32Array(numVerts * 3)
  const uvs = new Float32Array(numVerts * 2)
  const invalid = new Uint8Array(numVerts)
  // Roblox の自動生成メッシュには稀に NaN/Inf の退化頂点が混じる（数千頂点に数個）。
  // そのままだと bounding box/sphere が NaN になり frustum culling でメッシュごと
  // 消える。非有限な頂点は invalid に印を付け（参照する面は filterValidFaces で除去）、
  // 位置は直近の有効頂点へ畳んで（無ければ原点）positions を有限に保つ。
  let lastX = 0
  let lastY = 0
  let lastZ = 0
  for (let i = 0; i < numVerts; i += 1) {
    const base = offset + i * vertexSize
    let px = view.getFloat32(base, true)
    let py = view.getFloat32(base + 4, true)
    let pz = view.getFloat32(base + 8, true)
    if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz)) {
      invalid[i] = 1
      px = lastX
      py = lastY
      pz = lastZ
    } else {
      lastX = px
      lastY = py
      lastZ = pz
    }
    positions[i * 3] = px
    positions[i * 3 + 1] = py
    positions[i * 3 + 2] = pz
    // 法線・UV も非有限は 0 に倒して NaN 伝播を断つ（退化頂点は不可視なので影響なし）。
    const nx = view.getFloat32(base + 12, true)
    const ny = view.getFloat32(base + 16, true)
    const nz = view.getFloat32(base + 20, true)
    normals[i * 3] = Number.isFinite(nx) ? nx : 0
    normals[i * 3 + 1] = Number.isFinite(ny) ? ny : 0
    normals[i * 3 + 2] = Number.isFinite(nz) ? nz : 0
    const u = view.getFloat32(base + 24, true)
    const v = view.getFloat32(base + 28, true)
    uvs[i * 2] = Number.isFinite(u) ? u : 0
    // Roblox バイナリメッシュの UV は V=0 が画像上端（DirectX 規約）。three.js は
    // flipY=true（V=0 が下端）で読むため 1-V へ正規化する（v1.00 テキストと同じ補正）。
    // 補正しないとカットアウト系テクスチャ（木の枝カード等）が空白域をサンプリング
    // して alphaTest で全て破棄され、メッシュごと消えたように見える。
    uvs[i * 2 + 1] = Number.isFinite(v) ? 1 - v : 0
  }
  return { positions, normals, uvs, invalid }
}

/** バイナリ面配列（各面 3 × uint32 のインデックス）を読み出す。 */
const readFaces = (
  view: DataView,
  offset: number,
  numFaces: number,
): Uint32Array => {
  const indices = new Uint32Array(numFaces * 3)
  for (let i = 0; i < numFaces; i += 1) {
    const base = offset + i * 12
    indices[i * 3] = view.getUint32(base, true)
    indices[i * 3 + 1] = view.getUint32(base + 4, true)
    indices[i * 3 + 2] = view.getUint32(base + 8, true)
  }
  return indices
}

/**
 * 範囲外インデックス（>= numVerts）または退化頂点（invalid）を参照する三角形を捨てる。
 * 壊れた index による WebGL の out-of-range draw と、退化頂点による不正面を防ぐ。
 */
const filterValidFaces = (
  indices: Uint32Array,
  numVerts: number,
  invalid: Uint8Array,
): Uint32Array => {
  const kept: number[] = []
  for (let i = 0; i + 2 < indices.length; i += 3) {
    const a = indices[i]
    const b = indices[i + 1]
    const c = indices[i + 2]
    if (a >= numVerts || b >= numVerts || c >= numVerts) {
      continue
    }
    if (invalid[a] || invalid[b] || invalid[c]) {
      continue
    }
    kept.push(a, b, c)
  }
  return new Uint32Array(kept)
}

/**
 * LOD0 面を有効面だけに絞り込む。面があったのに全滅した場合は描画不能とみなして
 * throw し、呼び出し側の box フォールバックへ委ねる（空ジオメトリで不可視化しない）。
 */
const finalizeIndices = (
  lod0: Uint32Array,
  numVerts: number,
  invalid: Uint8Array,
): Uint32Array => {
  const filtered = filterValidFaces(lod0, numVerts, invalid)
  if (lod0.length > 0 && filtered.length === 0) {
    throw new Error(
      'Roblox mesh(binary): 有効な面がありません（全面が範囲外/退化）',
    )
  }
  return filtered
}

/**
 * 面配列を LOD0（最高詳細）だけに絞る。lodOffsets は面インデックスの境界で、
 * LOD0 は [lodOffsets[0], lodOffsets[1]) の範囲。LOD 情報が無い/不正なら全面を返す。
 */
const sliceLod0 = (
  indices: Uint32Array,
  lodOffsets: Uint32Array | null,
): Uint32Array => {
  if (!lodOffsets || lodOffsets.length < 2) {
    return indices
  }
  const startFace = lodOffsets[0]
  const endFace = lodOffsets[1]
  if (
    !Number.isFinite(startFace) ||
    !Number.isFinite(endFace) ||
    endFace <= startFace ||
    endFace * 3 > indices.length
  ) {
    return indices
  }
  return indices.slice(startFace * 3, endFace * 3)
}

/**
 * v2.00 バイナリをパースする。
 * ヘッダ(=12byte): u16 headerSize, u8 vertexSize, u8 faceSize, u32 numVerts, u32 numFaces
 */
const parseV2 = (view: DataView, body: number): ParsedMesh => {
  ensureRange(view, body, 12, 'v2 header')
  const headerSize = view.getUint16(body, true)
  const vertexSize = view.getUint8(body + 2)
  const faceSize = view.getUint8(body + 3)
  const numVerts = view.getUint32(body + 4, true)
  const numFaces = view.getUint32(body + 8, true)
  if (vertexSize < 32) {
    throw new Error(`Roblox mesh v2: vertexSize が小さすぎます: ${vertexSize}`)
  }
  if (faceSize !== 12) {
    throw new Error(`Roblox mesh v2: faceSize=${faceSize} は未対応です`)
  }
  const vertOffset = body + headerSize
  ensureRange(view, vertOffset, numVerts * vertexSize, 'v2 vertices')
  const { positions, normals, uvs, invalid } = readVertices(
    view,
    vertOffset,
    numVerts,
    vertexSize,
  )
  const faceOffset = vertOffset + numVerts * vertexSize
  ensureRange(view, faceOffset, numFaces * 12, 'v2 faces')
  const indices = readFaces(view, faceOffset, numFaces)
  return {
    positions,
    normals,
    uvs,
    indices: finalizeIndices(indices, numVerts, invalid),
  }
}

/**
 * v3.00 / v3.01 バイナリをパースする。
 * ヘッダ(=16byte): u16 headerSize, u8 vertexSize, u8 faceSize,
 *                  u16 lodSize, u16 numLODs, u32 numVerts, u32 numFaces
 * 配列順: 頂点 → 面 → LOD オフセット(u32 × numLODs)
 */
const parseV3 = (view: DataView, body: number): ParsedMesh => {
  ensureRange(view, body, 16, 'v3 header')
  const headerSize = view.getUint16(body, true)
  const vertexSize = view.getUint8(body + 2)
  const faceSize = view.getUint8(body + 3)
  const numLODs = view.getUint16(body + 6, true)
  const numVerts = view.getUint32(body + 8, true)
  const numFaces = view.getUint32(body + 12, true)
  if (vertexSize < 32) {
    throw new Error(`Roblox mesh v3: vertexSize が小さすぎます: ${vertexSize}`)
  }
  if (faceSize !== 12) {
    throw new Error(`Roblox mesh v3: faceSize=${faceSize} は未対応です`)
  }
  const vertOffset = body + headerSize
  ensureRange(view, vertOffset, numVerts * vertexSize, 'v3 vertices')
  const { positions, normals, uvs, invalid } = readVertices(
    view,
    vertOffset,
    numVerts,
    vertexSize,
  )
  const faceOffset = vertOffset + numVerts * vertexSize
  ensureRange(view, faceOffset, numFaces * 12, 'v3 faces')
  const indices = readFaces(view, faceOffset, numFaces)
  let lodOffsets: Uint32Array | null = null
  if (numLODs >= 2) {
    const lodOffset = faceOffset + numFaces * 12
    ensureRange(view, lodOffset, numLODs * 4, 'v3 LODs')
    lodOffsets = new Uint32Array(numLODs)
    for (let i = 0; i < numLODs; i += 1) {
      lodOffsets[i] = view.getUint32(lodOffset + i * 4, true)
    }
  }
  return {
    positions,
    normals,
    uvs,
    indices: finalizeIndices(sliceLod0(indices, lodOffsets), numVerts, invalid),
  }
}

/**
 * v4.00 / v4.01 バイナリをパースする。
 * ヘッダ(=24byte): u16 headerSize, u16 lodType, u32 numVerts, u32 numFaces,
 *                  u16 numLODs, u16 numBones, u32 boneNamesBufSize,
 *                  u16 numSubsets, u8 numHighQualityLODs, u8 unused
 * 配列順: 頂点(40byte) → [ボーンがあれば エンベロープ 8byte×頂点] → 面 → LOD(u32 × numLODs) → ...
 * 静的描画に必要な頂点・面・LOD0 のみ使い、ボーン/サブセット等は読み飛ばす。
 */
const parseV4 = (view: DataView, body: number): ParsedMesh => {
  ensureRange(view, body, 16, 'v4 header')
  const headerSize = view.getUint16(body, true)
  const numVerts = view.getUint32(body + 4, true)
  const numFaces = view.getUint32(body + 8, true)
  const numLODs = view.getUint16(body + 12, true)
  const numBones = view.getUint16(body + 14, true)
  // v4 頂点は固定 40byte（pos12 + normal12 + uv8 + tangent4 + rgba4）。
  const VERTEX_SIZE = 40
  const vertOffset = body + headerSize
  ensureRange(view, vertOffset, numVerts * VERTEX_SIZE, 'v4 vertices')
  const { positions, normals, uvs, invalid } = readVertices(
    view,
    vertOffset,
    numVerts,
    VERTEX_SIZE,
  )
  let cursor = vertOffset + numVerts * VERTEX_SIZE
  // スキン付きメッシュは頂点ごとに 8byte のエンベロープ（ボーン index/weight）が続く。
  if (numBones > 0) {
    ensureRange(view, cursor, numVerts * 8, 'v4 envelopes')
    cursor += numVerts * 8
  }
  ensureRange(view, cursor, numFaces * 12, 'v4 faces')
  const indices = readFaces(view, cursor, numFaces)
  const faceEnd = cursor + numFaces * 12
  let lodOffsets: Uint32Array | null = null
  if (numLODs >= 2) {
    ensureRange(view, faceEnd, numLODs * 4, 'v4 LODs')
    lodOffsets = new Uint32Array(numLODs)
    for (let i = 0; i < numLODs; i += 1) {
      lodOffsets[i] = view.getUint32(faceEnd + i * 4, true)
    }
  }
  return {
    positions,
    normals,
    uvs,
    indices: finalizeIndices(sliceLod0(indices, lodOffsets), numVerts, invalid),
  }
}

/** ArrayBuffer 中のテキスト先頭から version を抜き取り、形式ごとにパースする。 */
const parseMesh = (buffer: ArrayBuffer): ParsedMesh => {
  const bytes = new Uint8Array(buffer)
  // UTF-8 として版識別子だけ先読み。v2+ では先頭以外がバイナリだが、version 行は常にテキスト。
  const probe = new TextDecoder('utf-8').decode(buffer.slice(0, 64))
  const version = readVersion(probe)
  if (version === '1.00' || version === '1.01') {
    const text = new TextDecoder('utf-8').decode(buffer)
    return parseV1(text, version)
  }
  const view = new DataView(buffer)
  const body = binaryBodyOffset(bytes)
  const major = parseInt(version.split('.')[0], 10)
  switch (major) {
    case 2:
      return parseV2(view, body)
    case 3:
      return parseV3(view, body)
    case 4:
      return parseV4(view, body)
    default:
      throw new Error(`Roblox mesh: 未対応 version ${version}`)
  }
}

/**
 * URL から Roblox .mesh を fetch して BufferGeometry を返す。
 * バイナリ形式はインデックス付きジオメトリ、テキスト v1 は非インデックス
 * （各 face が独立 vertex として記録されているため）として構築する。
 */
export const loadRobloxMeshGeometry = async (
  url: string,
): Promise<THREE.BufferGeometry> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(
      `Roblox mesh fetch 失敗 ${response.status} ${response.statusText}: ${url}`,
    )
  }
  const buffer = await response.arrayBuffer()
  const parsed = parseMesh(buffer)
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(parsed.positions, 3),
  )
  geometry.setAttribute('normal', new THREE.BufferAttribute(parsed.normals, 3))
  geometry.setAttribute('uv', new THREE.BufferAttribute(parsed.uvs, 2))
  if (parsed.indices) {
    geometry.setIndex(new THREE.BufferAttribute(parsed.indices, 1))
  }
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()
  return geometry
}
